import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot
} from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { 
  FaPlus, FaSearch, FaFilter, FaChevronLeft, FaChevronRight, FaUser, FaEnvelope,
  FaPhone, FaUsers, FaBoxOpen, FaStore, FaUserCircle, FaPencilAlt, FaTimesCircle, FaUndo
} from "react-icons/fa";

// --- Import the updated CSS ---
import "./Customers.css";

const Customers = () => {
  // --- EXISTING STATE ---
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", address: "", password: "", location: "", app_type: "", hub: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // --- STATE FOR FILTERS AND SORTING ---
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterAppType, setFilterAppType] = useState("all");
  const [filterHub, setFilterHub] = useState("all");

  const customersCollectionRef = collection(db, "customers");

  // --- Effect to fetch ALL customers from Firestore ---
  useEffect(() => {
    setLoading(true);
    // This query now fetches all customers, ordered by name. Filtering is done on the client side.
    const q = query(customersCollectionRef, orderBy("name"));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const customerList = querySnapshot.docs.map((doc) => ({ ...doc.data(), firestoreId: doc.id }));
      setCustomers(customerList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching customers:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // --- Dynamic Filter Options (useMemo for performance) ---
  const uniqueAppTypes = useMemo(() => {
    const types = new Set(customers.map(c => c.app_type).filter(Boolean));
    return ["all", ...Array.from(types)];
  }, [customers]);

  const uniqueHubs = useMemo(() => {
    const hubs = new Set(customers.map(c => c.hub).filter(Boolean));
    return ["all", ...Array.from(hubs)];
  }, [customers]);

  // --- Combined Filtering and Sorting Logic ---
  const filteredCustomers = useMemo(() => {
    let filtered = [...customers];

    // Apply filters
    if (filterAppType !== "all") {
      filtered = filtered.filter(c => c.app_type === filterAppType);
    }
    if (filterHub !== "all") {
      filtered = filtered.filter(c => c.hub === filterHub);
    }

    // Apply search term
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(customer =>
        Object.values(customer).some(value =>
          String(value).toLowerCase().includes(lowercasedTerm)
        )
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortBy] || "";
      const bValue = b[sortBy] || "";
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [customers, searchTerm, sortBy, sortOrder, filterAppType, filterHub]);

  // --- Reset all filters to their default state ---
  const handleResetFilters = () => {
    setSearchTerm("");
    setSortBy("name");
    setSortOrder("asc");
    setFilterAppType("all");
    setFilterHub("all");
    setCurrentPage(1);
  };
  
  // --- PAGINATION LOGIC ---
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const currentCustomers = filteredCustomers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // --- CRUD AND MODAL FUNCTIONS (No changes needed in these) ---
  const generateNewCustomerId = () => {
    let highestNumber = 0;
    customers.forEach(customer => {
      if (customer.id && customer.id.startsWith('C-')) {
        const numberPart = parseInt(customer.id.split('-')[1], 10);
        if (!isNaN(numberPart) && numberPart > highestNumber) {
          highestNumber = numberPart;
        }
      }
    });
    return `C-${String(highestNumber + 1).padStart(4, "0")}`;
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setFormData({ name: "", email: "", mobile: "", address: "", password: "", location: "", app_type: "", hub: "" });
    setCurrentCustomer(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!isEditing && !formData.password)) {
      alert("Please fill in all required fields.");
      return;
    }
  
    setLoading(true);
    if (isEditing) {
      const customerDoc = doc(db, "customers", currentCustomer.firestoreId);
      const dataToUpdate = {
        name: formData.name, email: formData.email, mobile: formData.mobile,
        address: formData.address, location: formData.location, app_type: formData.app_type, hub: formData.hub,
      };
      await updateDoc(customerDoc, dataToUpdate);
    } else {
      const auth = getAuth();
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const newCustomerData = {
          id: generateNewCustomerId(), name: formData.name, email: formData.email, mobile: formData.mobile,
          address: formData.address, password: formData.password, location: formData.location,
          app_type: formData.app_type, hub: formData.hub, role: "user", createdAt: new Date()
        };
        await setDoc(doc(db, "customers", userCredential.user.uid), newCustomerData);
      } catch (error) {
        alert(`Error: ${error.message}`);
      }
    }
    setLoading(false);
    closeModal();
  };
  
  const handleEdit = (customer) => {
    setIsEditing(true);
    setCurrentCustomer(customer);
    setFormData({ ...customer, password: "" });
    openModal();
  };

  const handleDelete = async (firestoreId) => {
    if (window.confirm("Are you sure?")) {
      await deleteDoc(doc(db, "customers", firestoreId));
    }
  };

  const viewCustomerDetails = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Customers...</p>
      </div>
    );
  }

  return (
    <div className="customers-container">
      <header className="customers-header">
        <h1 className="customers-title"><FaUsers /> Customer Management</h1>
        <button className="add-button" onClick={openModal}><FaPlus /> Add Customer</button>
      </header>

      <div className="card filter-card">
        <div className="filter-controls">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input type="text" placeholder="Search anything..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input"/>
          </div>
          <div className="filter-group">
            <FaBoxOpen />
            <select value={filterAppType} onChange={(e) => setFilterAppType(e.target.value)}>
              {uniqueAppTypes.map(type => <option key={type} value={type}>{type === 'all' ? 'All App Types' : type}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <FaStore />
            <select value={filterHub} onChange={(e) => setFilterHub(e.target.value)}>
              {uniqueHubs.map(hub => <option key={hub} value={hub}>{hub === 'all' ? 'All Hubs' : hub}</option>)}
            </select>
          </div>
          <button className="reset-button" onClick={handleResetFilters}><FaUndo /> Reset</button>
        </div>
      </div>

      <div className="card table-card">
        <div className="table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th onClick={() => { setSortBy('name'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Name</th>
                <th onClick={() => { setSortBy('email'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Contact</th>
                <th onClick={() => { setSortBy('app_type'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>App Type</th>
                <th onClick={() => { setSortBy('hub'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Hub</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.length > 0 ? currentCustomers.map((customer) => (
                <tr key={customer.firestoreId}>
                  <td data-label="Name">{customer.name}</td>
                  <td data-label="Contact">
                    <div className="contact-info">
                      <span>{customer.email}</span>
                      <span>{customer.mobile || "N/A"}</span>
                    </div>
                  </td>
                  <td data-label="App Type">{customer.app_type || "N/A"}</td>
                  <td data-label="Hub">{customer.hub || "N/A"}</td>
                  <td data-label="Actions">
                    <div className="action-buttons">
                      <button className="action-button view" onClick={() => viewCustomerDetails(customer)} title="View"><FaUserCircle /></button>
                      <button className="action-button edit" onClick={() => handleEdit(customer)} title="Edit"><FaPencilAlt /></button>
                      <button className="action-button delete" onClick={() => handleDelete(customer.firestoreId)} title="Delete"><FaTimesCircle /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5" className="no-results">No customers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}><FaChevronLeft /></button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}><FaChevronRight /></button>
          </div>
        )}
      </div>

      {isModalOpen && <Modal closeModal={closeModal} handleSubmit={handleSubmit} formData={formData} handleInputChange={handleInputChange} isEditing={isEditing}/>}
      {showDetailsModal && selectedCustomer && <DetailsModal customer={selectedCustomer} closeModal={() => setShowDetailsModal(false)} />}
    </div>
  );
};

// --- MODAL COMPONENTS (Separated for clarity) ---

const Modal = ({ closeModal, handleSubmit, formData, handleInputChange, isEditing }) => (
  <div className="modal-overlay" onClick={closeModal}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2>{isEditing ? "Edit Customer" : "Add New Customer"}</h2>
        <button className="modal-close" onClick={closeModal}>×</button>
      </div>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="form-grid">
          <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Customer Name *" required />
          <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address *" required disabled={isEditing}/>
          <input type="tel" name="mobile" value={formData.mobile} onChange={handleInputChange} placeholder="Mobile Number" />
          <input type="text" name="app_type" value={formData.app_type} onChange={handleInputChange} placeholder="App Type (e.g., Grocery)" />
          <input type="text" name="hub" value={formData.hub} onChange={handleInputChange} placeholder="Hub Name" />
          {!isEditing && <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Password *" required={!isEditing}/>}
          <textarea name="address" value={formData.address} onChange={handleInputChange} placeholder="Full Address" rows="3" className="full-width"></textarea>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={closeModal}>Cancel</button>
          <button type="submit" className="submit-button">{isEditing ? "Update Customer" : "Create Customer"}</button>
        </div>
      </form>
    </div>
  </div>
);

const DetailsModal = ({ customer, closeModal }) => (
  <div className="modal-overlay" onClick={closeModal}>
    <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2>Customer Details</h2>
        <button className="modal-close" onClick={closeModal}>×</button>
      </div>
      <div className="details-grid">
        <div><label>Customer ID</label><span>{customer.id || "N/A"}</span></div>
        <div><label>Name</label><span>{customer.name}</span></div>
        <div><label>Email</label><span>{customer.email}</span></div>
        <div><label>Mobile</label><span>{customer.mobile || "Not provided"}</span></div>
        <div><label>App Type</label><span>{customer.app_type || "N/A"}</span></div>
        <div><label>Hub</label><span>{customer.hub || "N/A"}</span></div>
        <div className="full-width"><label>Address</label><span>{customer.address || "Not provided"}</span></div>
      </div>
    </div>
  </div>
);

export default Customers;